import React, { useEffect, useState } from 'react';
import {
  Alert, AlertDescription, AlertIcon, Box, Button, Heading, HStack, Input, SimpleGrid, Text, VStack, useToast,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { EmailColourSlot, EmailPalette } from '../types/api';
import { DEFAULT_PALETTE, SLOT_LABELS } from '../utils/emailBlocks';

const SLOTS = Object.keys(DEFAULT_PALETTE) as EmailColourSlot[];
const SLOT_HELP: Record<EmailColourSlot, string> = {
  primary: 'Ligne de séparation, liens, boutons',
  secondary: 'Boutons d\'action, texte d\'accent',
  accent: 'Texte d\'accent, boutons',
  neutral: 'Texte secondaire, boutons discrets',
};

const errorText = (err: any): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join(' ; ');
  return err?.message || 'Erreur inconnue';
};

const EmailPalettePage: React.FC = () => {
  const toast = useToast();
  const qc = useQueryClient();
  const paletteQ = useQuery({ queryKey: ['email-palette'], queryFn: () => apiService.getEmailPalette() });
  const [draft, setDraft] = useState<EmailPalette>(DEFAULT_PALETTE);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (paletteQ.data) setDraft(paletteQ.data);
  }, [paletteQ.data]);

  const save = useMutation({
    mutationFn: () => apiService.updateEmailPalette(draft),
    onSuccess: (saved) => {
      setError(null);
      setDraft(saved);
      qc.setQueryData(['email-palette'], saved);
      toast({ title: 'Palette enregistrée', status: 'success', duration: 4000 });
    },
    // The message comes from the server (same rule, same wording as the API).
    onError: (err) => setError(errorText(err)),
  });

  const dirty = !!paletteQ.data && SLOTS.some((s) => (paletteQ.data as EmailPalette)[s] !== draft[s]);

  return (
    <Box maxW="3xl">
      <Heading size="lg" mb={2}>Palette des e-mails</Heading>
      <Text color="gray.600" mb={4}>
        Quatre couleurs pour toute l'application. Les blocs des e-mails choisissent l'une de ces quatre couleurs.
      </Text>

      <Alert status="warning" borderRadius="md" mb={4} data-testid="palette-notice">
        <AlertIcon />
        <AlertDescription fontSize="sm">
          Un changement s'applique à tous les envois futurs de tous les événements, y compris ceux déjà préparés.
          Les e-mails déjà envoyés ne changent pas.
        </AlertDescription>
      </Alert>

      {error && (
        <Alert status="error" borderRadius="md" mb={4} data-testid="palette-error">
          <AlertIcon />
          <AlertDescription fontSize="sm">{error}</AlertDescription>
        </Alert>
      )}

      <VStack align="stretch" spacing={3} mb={6}>
        {SLOTS.map((slot) => (
          <HStack key={slot} spacing={3} data-testid={`palette-row-${slot}`}>
            <Box w="36px" h="36px" borderRadius="md" borderWidth="1px" bg={draft[slot]} flexShrink={0} data-testid={`palette-swatch-${slot}`} />
            <Box w="170px" flexShrink={0}>
              <Text fontWeight="semibold" fontSize="sm">{SLOT_LABELS[slot]}</Text>
              <Text fontSize="xs" color="gray.500">{SLOT_HELP[slot]}</Text>
            </Box>
            <Input
              type="color"
              w="56px"
              p={1}
              value={/^#[0-9a-fA-F]{6}$/.test(draft[slot]) ? draft[slot] : '#000000'}
              onChange={(e) => setDraft({ ...draft, [slot]: e.target.value })}
              aria-label={`Couleur ${SLOT_LABELS[slot]} (sélecteur)`}
            />
            <Input
              w="120px"
              fontFamily="mono"
              value={draft[slot]}
              maxLength={7}
              onChange={(e) => setDraft({ ...draft, [slot]: e.target.value.trim() })}
              aria-label={`Couleur ${SLOT_LABELS[slot]}`}
              data-testid={`palette-input-${slot}`}
            />
          </HStack>
        ))}
      </VStack>

      <Heading size="sm" mb={2}>Aperçu</Heading>
      <Box borderWidth="1px" borderRadius="md" p={4} bg="white" mb={6} data-testid="palette-sample">
        <Text color="#333333" mb={2}>
          Texte courant, <Text as="span" color={draft.accent}>texte d'accent</Text>,{' '}
          <Text as="span" color={draft.neutral}>texte neutre</Text> et{' '}
          <Text as="span" color={draft.primary} textDecoration="underline">un lien</Text>.
        </Text>
        <Box h="1px" bg={draft.primary} w="80%" mx="auto" my={3} />
        <HStack justify="center" spacing={3}>
          {SLOTS.map((slot) => (
            <Box key={slot} bg={draft[slot]} color="white" px={5} py={2} borderRadius="md" fontSize="sm">
              {SLOT_LABELS[slot]}
            </Box>
          ))}
        </HStack>
      </Box>

      <SimpleGrid columns={2} spacing={3} maxW="sm">
        <Button colorScheme="blue" onClick={() => save.mutate()} isLoading={save.isPending} isDisabled={!dirty}>
          Enregistrer
        </Button>
        <Button variant="outline" onClick={() => { setDraft(paletteQ.data || DEFAULT_PALETTE); setError(null); }} isDisabled={!dirty}>
          Annuler
        </Button>
      </SimpleGrid>
    </Box>
  );
};

export default EmailPalettePage;
